<template>
  <div style="position:relative;" data-locator="signing-checklist-table">
    <template v-if="searchQueryPending">
      <v-skeleton-loader type="table-thead, table-heading, table-tbody"/>
    </template>
    <template v-else>
      <v-row v-if="$vuetify.display.mdAndUp"
            class="SigningChecklistTable-header flex-nowrap signing-checklist-tab-header"
            align="center"
      >
        <v-col :cols="hasSigningOrder ? 4 : 5">
          <v-row class="align-center" no-gutters>
            <v-col v-if="isPm" cols="auto">
              <v-checkbox
                :model-value="isEveryStepsSelected"
                readonly
                hide-details
                class="mt-0 pt-0 mr-2"
                :ripple="false"
                data-locator="select-all-checkbox"
                @click.stop="onSelectedStepsClick"
              />
            </v-col>
            <v-col cols="auto">
              <TruncatedText class="text-uppercase text--small text--bold">
                {{ $t('project.signing-checklist.list.SigningChecklistTable.header.documents') }}
              </TruncatedText>
            </v-col>
            <v-col>
              <AppTooltip>
                <template #activator="{props}">
                  <AppButton icon
                            size="small"
                            :x-padding="0"
                            v-bind="props"
                            @click="onExpandOrder(true)"
                  >
                    <font-awesome-icon :icon="['fas', 'up-right-and-down-left-from-center']"
                                      class="text-caption"
                                      :transform="{ rotate: -45 }"
                    ></font-awesome-icon>
                  </AppButton>
                </template>
                <span>{{$t('project.signing-checklist.list.SigningChecklistTable.expandTooltip')}}</span>
              </AppTooltip>
              <AppTooltip>
                <template #activator="{props}">
                  <AppButton icon
                            size="small"
                            :x-padding="0"
                            v-bind="props"
                            @click="onExpandOrder(false)"
                  >
                    <font-awesome-icon :icon="['fas', 'down-left-and-up-right-to-center']"
                                      class="text-caption"
                                      :transform="{ rotate: -45 }"
                    ></font-awesome-icon>
                  </AppButton>
                </template>
                <span>{{$t('project.signing-checklist.list.SigningChecklistTable.compressTooltip')}}</span>
              </AppTooltip>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="2">
          <template v-if="isPm">
            <AppTooltip>
              <template #activator="{props}">
                <div v-bind="props">
                  <AppSelect v-model="headerSelectedColumn"
                            no-label
                            return-object
                            :items="headerSelectItems"
                            item-title="text"
                            item-value="value"
                  >
                    <template #selection="{ item }">
                      <p class="text-truncate text-uppercase mb-0 text--small text--bold">
                        {{ item.title }}
                      </p>
                    </template>
                  </AppSelect>
                </div>
              </template>
              <span> {{ headerSelectedColumn.text }}  </span>
            </AppTooltip>
          </template>
          <template v-else>
            <p class="text-uppercase text--small text--bold">
              {{ $t('project.signing-checklist.list.SigningChecklistTable.header.signatories') }}
            </p>
          </template>
        </v-col>
        <v-col cols="2">
          <TruncatedText class="text-uppercase text--small text--bold">
            {{ $t('project.signing-checklist.list.SigningChecklistTable.header.validators') }}
          </TruncatedText>
        </v-col>
        <v-col cols="2">
          <TruncatedText class="text-uppercase text--small text--bold">
            {{ $t('project.signing-checklist.list.SigningChecklistTable.header.status') }}
          </TruncatedText>
        </v-col>
        <v-col v-if="hasSigningOrder" cols="1">
          <TruncatedText class="text-uppercase text--small text--bold">
            {{ $t('project.signing-checklist.list.SigningChecklistTable.header.signOrder') }}
          </TruncatedText>
        </v-col>
        <v-col class="text-right" cols="1">
          <TruncatedText class="text-uppercase text--small text--bold">
            {{ $t('project.signing-checklist.list.SigningChecklistTable.header.options') }}
          </TruncatedText>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pt-0">
          <div v-if="isSigningChecklistFiltered && filteredSigningChecklist && filteredSigningChecklist.length === 0" class="mt-8">
            <DataTableFilterNoData />
          </div>
            <draggable v-model="signingChecklistModel"
                     item-key="id"
                    :forceFallback="true"
                    :group="{name: 'topLevel', pull: false, put: false}"
                    :emptyInsertThreshold="100"
                    :delay="500"
                    :delayOnTouchOnly="true"
                    :disabled="!isPm || isSigningChecklistFiltered"
                    handle=".SigningChecklistTable-dragIcon"
                    @change="preparePostMoveStep"
                    @start="dragging = true"
                    @end="dragging = false"
          >
            <template #item="{ element: title }">
            <SigningChecklistTableTitle
                                        :id="`topTitle-${title.id}`"
                                        :key="`topTitle-${title.id}`"
                                        :item="title"
                                        :isSigningChecklistFiltered="isSigningChecklistFiltered"
                                        :mnemo="mnemo"
                                        v-model:headerSelectedColumn="headerSelectedColumn"
                                        :header-select-items="headerSelectItems"
                                        :floatingAdd="tableElementMouseEntered === `${title.type}-${title.id}`"
                                        :elementEntered="tableElementMouseEntered"
                                        :hasSigningOrder="hasSigningOrder"
                                        @newElementEntered="onNewElementEntered"
                                        @save-as-template="envelopeToSaveAsTemplate = $event"
              />
            </template>
          </draggable>
        </v-col>
      </v-row>
    </template>
    <SigningChecklistDeleteStepDialog
      v-if="deleteStepModal"
      :ids="deleteStepModal"
      :mnemo="mnemo"
      @close="stepDeletionHidden"
      @confirm="stepDeletionConfirm"
    />
    <SigningChecklistDeleteTitleDialog
      v-if="deleteTitleModal"
      @close="titleDeletionHidden"
      @confirm="titleDeletionConfirm"
    />

    <SigningChecklistCancelStepDialog
      v-if="cancelStepModal"
      :ids="cancelStepModal?.ids"
      :mnemo="mnemo"
      @close="cancelStepHidden"
      @confirm="cancelStepConfirm"
    />

    <SigningChecklistVoidedToDraftStepDialog
      v-if="voidedToDraftStepModal"
      :count="voidedToDraftStepModal?.ids?.length"
      @close="voidedToDraftStepHidden"
      @confirm="voidedToDraftStepConfirm"
    />
    <SigningChecklistSendDocumentsDialog
      v-if="sendSignStepModal"
      :mnemo="mnemo"
      @close="sendSignStepHidden"
      @confirm="sendSignStepConfirm"
    />
    <SaveDocumentAsTemplateDialog v-if="envelopeToSaveAsTemplate"
                                  :envelope="envelopeToSaveAsTemplate"
                                  @close="envelopeToSaveAsTemplate = null"
    />
    <SwapFilesDialog
      v-if="swapFiles"
      :mnemo="mnemo"
      @cancel-swap="showCancelDialog = true"
      @close="resetSwapFiles"
      @confirm="onSwapFileConfirm"
    />
    <SigningChecklistPrepareCancelDialog
      v-if="showCancelDialog"
      @close="showCancelDialog = false"
      @confirm="resetSwapFiles"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'

import AppSelect from '@/common/AppSelect.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import DataTableFilterNoData from '@/common/filters/DataTableFilterNoData.vue'
import { track } from '@/common/pendo/agent'
import TruncatedText from '@/common/TruncatedText.vue'
import { findStepParent } from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import SigningChecklistPrepareCancelDialog from '@/project/signing-checklist/dialogs/SigningChecklistPrepareCancelDialog.vue'
import SigningChecklistSendDocumentsDialog from '@/project/signing-checklist/dialogs/SigningChecklistSendDocumentsDialog.vue'
import SwapFilesDialog from '@/project/signing-checklist/dialogs/SwapFilesDialog.vue'
import SaveDocumentAsTemplateDialog from '@/project/signing-checklist/envelope-templates/dialogs/SaveDocumentAsTemplateDialog.vue'
import SigningChecklistTableTitle from '@/project/signing-checklist/list/SigningChecklistTableTitle.vue'
import {
  CANCEL_STEP,
  CANCEL_STEP_MULTIPLE,
  DELETE_STEP,
  DELETE_TITLE,
  DELETE_STEP_MULTIPLE,
  POST_MOVE_STEP,
  REFRESH_SIGNING_CHECKLIST,
  REFRESH_STEP,
  RESET_STEP,
  RESET_STEP_MULTIPLE,
  SEND_SIGN_NOW,
  SEND_SIGN_NOW_MULTIPLE,
  SWAP_STEP_FILES,
  VALIDATE_THEN_SEND_NOW,
  VALIDATE_THEN_SEND_NOW_MULTIPLE,
} from '@/store/modules/signing-checklist/action_types'
import {
  SET_SIGNING_CHECKLIST,
  SET_ADD_SELECTED_STEPS,
  SET_REMOVE_SELECTED_STEPS,
  SET_DELETE_STEP_MODAL,
  SET_SWAP_FILES,
  SET_CANCEL_STEP_MODAL,
  SET_SEND_SIGN_STEP_MODAL,
  SET_VOIDED_TO_DRAFT_STEP_MODAL,
  SET_DELETE_TITLE_MODAL,
  ADD_REFRESH_STEP_PENDING,
  SET_REFRESH_STEP_PENDING_IDS,
  SET_SELECTED_STEPS,
  SET_EXPAND_ALL_SIGNING_CHECKLIST,
} from '@/store/modules/signing-checklist/mutation_types'

import SigningChecklistCancelStepDialog from './SigningChecklistCancelStepDialog.vue'
import SigningChecklistDeleteStepDialog from './SigningChecklistDeleteStepDialog.vue'
import SigningChecklistDeleteTitleDialog from './SigningChecklistDeleteTitleDialog.vue'
import SigningChecklistVoidedToDraftStepDialog from './SigningChecklistVoidedToDraftStepDialog.vue'

export default {
  name: 'SigningChecklistTable',
  components: {
    AppButton,
    AppSelect,
    AppTooltip,
    DataTableFilterNoData,
    SaveDocumentAsTemplateDialog,
    SigningChecklistCancelStepDialog,
    SigningChecklistDeleteStepDialog,
    SigningChecklistDeleteTitleDialog,
    SigningChecklistPrepareCancelDialog,
    SigningChecklistSendDocumentsDialog,
    SigningChecklistTableTitle,
    SigningChecklistVoidedToDraftStepDialog,
    SwapFilesDialog,
    TruncatedText,
    draggable,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      headerSelectItems: [
        {
          value: 'signatories',
          text: this.$t('project.signing-checklist.list.SigningChecklistTable.header.signatories'),
        },
        {
          value: 'recipients',
          text: this.$t('project.signing-checklist.list.SigningChecklistTable.header.recipients'),
        },
        {
          value: 'observers',
          text: this.$t('project.signing-checklist.list.SigningChecklistTable.header.observers'),
        },
      ],
      headerSelectedColumn: {
        value: 'signatories',
        text: this.$t('project.signing-checklist.list.SigningChecklistTable.header.signatories'),
      },
      tableElementMouseEntered: '',
      dragging: false,
      envelopeToSaveAsTemplate: null,
      showCancelDialog: false,
    }
  },
  computed: {
    ...mapState('signingChecklist', ['searchQuery', 'searchQueryPending', 'signingChecklist', 'refreshSigningChecklistPending', 'selectedSteps', 'deleteStepModal', 'swapFiles', 'cancelStepModal', 'sendSignStepModal', 'voidedToDraftStepModal', 'refreshStepPendingIds', 'deleteTitleModal', 'isExpandSigningChecklist']),
    ...mapGetters('signingChecklist', ['filteredSigningChecklist', 'flatSigningChecklist', 'isSigningChecklistFiltered']),
    isPm () {
      return this.$store.getters['room/isCurrentUserPm']
    },
    signingChecklistModel: {
      get () {
        return this.signingChecklist
      },
      set (value) {
        this.$store.commit(`signingChecklist/${SET_SIGNING_CHECKLIST}`, value)
      },
    },
    isEveryStepsSelected () {
      return this.displayedSigningChecklistSteps.every(el => {
        return this.selectedSteps.some(item => item.id === el.id && item.type === el.type)
      })
    },
    displayedSigningChecklistSteps () {
      const displayedList = this.isSigningChecklistFiltered ? this.filteredSigningChecklist : this.flatSigningChecklist
      return displayedList.filter(item => item.type === 'step')
    },
    hasSigningOrder () {
      return this.displayedSigningChecklistSteps.some((step) => step.signingGroup)
    },
  },
  methods: {
    ...mapActions('signingChecklist', [POST_MOVE_STEP, REFRESH_SIGNING_CHECKLIST, DELETE_STEP, DELETE_STEP_MULTIPLE, CANCEL_STEP, CANCEL_STEP_MULTIPLE, SEND_SIGN_NOW, SEND_SIGN_NOW_MULTIPLE, RESET_STEP, RESET_STEP_MULTIPLE, REFRESH_STEP, VALIDATE_THEN_SEND_NOW, VALIDATE_THEN_SEND_NOW_MULTIPLE, DELETE_TITLE, SWAP_STEP_FILES]),
    ...mapMutations('signingChecklist', [SET_DELETE_STEP_MODAL, SET_SWAP_FILES, SET_CANCEL_STEP_MODAL, SET_SEND_SIGN_STEP_MODAL, SET_VOIDED_TO_DRAFT_STEP_MODAL, ADD_REFRESH_STEP_PENDING, SET_REFRESH_STEP_PENDING_IDS, SET_DELETE_TITLE_MODAL, SET_SELECTED_STEPS, SET_EXPAND_ALL_SIGNING_CHECKLIST]),
    onNewElementEntered (elementString) {
      // It seems that when we're dragging or when user is a PM, we shouldn't re-render this component,
      // otherwise Draggable doesn't accept anything at the top level
      if (!this.dragging || !this.isPm) {
        this.tableElementMouseEntered = elementString
      }
    },
    async preparePostMoveStep (e) {
      if (e.moved) {
        const itemToMove = e.moved.element
        const itemToMoveIndexInFlattenSigningChecklist = this.flatSigningChecklist.findIndex(el => el.id === itemToMove.id && el.type === itemToMove.type)
        const data = itemToMoveIndexInFlattenSigningChecklist === 0
          ? { newPosition: 0 }
          : {
            prevId: this.flatSigningChecklist[itemToMoveIndexInFlattenSigningChecklist - 1].id,
            prevType: this.flatSigningChecklist[itemToMoveIndexInFlattenSigningChecklist - 1].type,
          }
        const trailItem = this.getTrailItem(itemToMove)
        data.newTrailId = trailItem.id
        data.newTrailType = trailItem.type
        try {
          await this.POST_MOVE_STEP({
            mnemo: this.mnemo,
            stepId: itemToMove.id,
            stepType: itemToMove.type,
            data,
          })
          toast.success(this.$t('project.signing-checklist.list.SigningChecklistTable.message.postMoveStepSuccess'))
        } catch (e) {
          toast.error(this.$t('project.signing-checklist.list.SigningChecklistTable.message.postMoveStepError'))
        }
        this.REFRESH_SIGNING_CHECKLIST({
          mnemo: this.mnemo,
        })
      }
    },
    getTrailItem (item) {
      if (item.type === 'step') {
        return item
      } else if (item.children.length === 0) {
        return item
      } else {
        return this.getTrailItem(item.children[item.children.length - 1])
      }
    },
    onSelectedStepsClick () {
      if (this.isEveryStepsSelected) {
        this.displayedSigningChecklistSteps.forEach(item => {
          this.$store.commit(`signingChecklist/${SET_REMOVE_SELECTED_STEPS}`, item)
        })
      } else {
       this.displayedSigningChecklistSteps.forEach(item => {
          this.$store.commit(`signingChecklist/${SET_ADD_SELECTED_STEPS}`, item)
        })
      }
    },
    onExpandOrder (value) {
      if (value === true || value === false) {
        const obj = { value: value, items: this.signingChecklistModel }
        this.SET_EXPAND_ALL_SIGNING_CHECKLIST(obj)
      }
    },
    async stepDeletionConfirm () {
      const deletionLength = this.deleteStepModal.length
      const ids = this.deleteStepModal
      if (deletionLength > 1) {
        await this.DELETE_STEP_MULTIPLE({ mnemo: this.mnemo, ids: this.deleteStepModal })
      } else {
        await this.DELETE_STEP({ mnemo: this.mnemo, stepId: this.deleteStepModal[0] })
      }
      toast.success(this.$t('project.signing-checklist.list.SigningChecklistTable.message.deleteStepSuccess', deletionLength))
      this.REFRESH_SIGNING_CHECKLIST({
        mnemo: this.mnemo,
      })
      if (this.selectedSteps.length !== 0) {
        this.SET_SELECTED_STEPS(
          this.selectedSteps.filter((selectedStep) => !ids.includes(selectedStep.id),
          ),
        )
      }
    },
    stepDeletionHidden () {
      this.SET_DELETE_STEP_MODAL(null)
    },
    async titleDeletionConfirm () {
      await this.DELETE_TITLE({
        mnemo: this.mnemo,
        id: this.deleteTitleModal,
      })
      this.REFRESH_SIGNING_CHECKLIST({
        mnemo: this.mnemo,
      })
    },
    titleDeletionHidden () {
      this.SET_DELETE_TITLE_MODAL(null)
    },
    async cancelStepConfirm () {
      const { ids } = this.cancelStepModal
      for (const id of ids) {
        this.ADD_REFRESH_STEP_PENDING(id)
      }
      try {
        if (ids.length > 1) {
          await this.CANCEL_STEP_MULTIPLE({ mnemo: this.mnemo, stepsIds: ids })
        } else {
          await this.CANCEL_STEP({ mnemo: this.mnemo, stepId: ids[0] })
        }
      } finally {
        this.onFinishStepPatch(ids)
      }
    },
    cancelStepHidden () {
      this.SET_CANCEL_STEP_MODAL(null)
    },
    async voidedToDraftStepConfirm () {
      const { steps, ids } = this.voidedToDraftStepModal
      const stepIds = steps.map((s) => s.id)
      for (const id of stepIds) {
        this.ADD_REFRESH_STEP_PENDING(id)
      }
      try {
        if (ids.length > 1) {
          await this.RESET_STEP_MULTIPLE({ mnemo: this.mnemo, ids })
        } else {
          const step = steps[0]
          await this.RESET_STEP({ mnemo: this.mnemo, envId: step.envelope.id })
          const computedParent = parent || findStepParent(step, this.signingChecklist)
          this.REFRESH_STEP({
            mnemo: this.mnemo,
            stepId: step.id,
            parent: computedParent,
          })
        }
      } finally {
        this.onFinishStepPatch(stepIds)
      }
    },
    voidedToDraftStepHidden () {
      this.SET_VOIDED_TO_DRAFT_STEP_MODAL(null)
    },
    async sendSignStepConfirm () {
      const { steps, ids } = this.sendSignStepModal
      for (const id of ids) {
        this.ADD_REFRESH_STEP_PENDING(id)
      }
      try {
        if (ids.length > 1) {
          await this.SEND_SIGN_NOW_MULTIPLE({ mnemo: this.mnemo, stepsIds: ids })
        } else {
          await this.SEND_SIGN_NOW({ mnemo: this.mnemo, step: steps[0] })
        }
      } finally {
        this.onFinishStepPatch(ids)
      }
    },
    sendSignStepHidden () {
      this.SET_SEND_SIGN_STEP_MODAL(null)
    },
    onFinishStepPatch (stepIds) {
      if (stepIds.some((id) => this.refreshStepPendingIds.includes(id))) {
        this.SET_REFRESH_STEP_PENDING_IDS(
          this.refreshStepPendingIds.filter((id) => !stepIds.includes(id)),
        )
      }
      // If there are patched steps included in the multi select
      // we should remove them from the selection
      if (this.selectedSteps.length !== 0) {
        this.SET_SELECTED_STEPS(
          this.selectedSteps.filter((selectedStep) => !stepIds.includes(selectedStep.id)),
        )
      }
    },
    async onSwapFileConfirm (data) {
      const { ids } = this.swapFiles

      for (const id of ids) {
        if (data.find(step => step.envelopeId === id)) { this.ADD_REFRESH_STEP_PENDING(id) }
      }

      try {
        await this.SWAP_STEP_FILES({
          mnemo: this.mnemo,
          data: data,
        })

        await this.REFRESH_SIGNING_CHECKLIST({
          mnemo: this.mnemo,
        })

        track('swap_success')
        toast.success(this.$t('project.signing-checklist.list.SigningChecklistTable.message.swapFileSuccess'))
      } catch {
        toast.error(this.$t('project.signing-checklist.list.SigningChecklistTable.message.swapFileError'))
      } finally {
        this.onFinishStepPatch(ids)
      }
    },
    resetSwapFiles () {
      this.SET_SWAP_FILES(null)
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";
/*Toggle class to fit the top with the multiple selection menu
* Need to be updated if the layout change
*/
  .signing-checklist-tab-header{
    position: sticky;
    background-color: #fff;
    z-index: 2;
  }
  .SigningChecklistTable-header-item {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 14px;
  }

  .SigningChecklistTable-refreshSigningChecklistLoader {
    position: absolute;
    z-index: 1;
    top: 5px;
    right: 0;
    left: 0;
    bottom: 0;
    background-color: rgba(#fff, .8);

    & .SigningChecklistTable-refreshSigningChecklistLoader-loader {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
.SigningChecklistTable-header-select {
  text-transform: uppercase;
  font-weight: bold;
}

:deep(.v-input--checkbox i) {
  font-size: 16px;
}
</style>
